// ===================================
// TRENCH QUEST - Onboarding Modal
// Shows intro, backstory, and tutorial for new players
// AI-generated unique backstory for each playthrough
// ===================================

import { FC, useState, useEffect } from 'react'
import { RetroWindow, RetroButton, PixelText } from '@/components/retro'
import { generateBackstoryViaCloud, GeneratedBackstory } from '@/services/gameMasterApi'
import type { GameSession } from '@/types'

interface Props {
  session: GameSession
  onClose: () => void
  onDontShowAgain: () => void
}

const STORAGE_KEY = 'trench-quest-skip-onboarding'

// Check if onboarding should be shown
export function shouldShowOnboarding(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'true'
  } catch {
    return true
  }
}

// Set the "don't show again" preference
export function setSkipOnboarding(skip: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, skip ? 'true' : 'false')
  } catch {
    // Ignore localStorage errors
  }
}

// Fallback backstory if AI generation fails
function getFallbackBackstory(session: GameSession): GeneratedBackstory {
  const difficulty = session.difficulty
  const startingAmount = session.startingAmount
  const goalAmount = session.goal.targetAmount
  const multiplier = (goalAmount / startingAmount).toFixed(0)
  const daysLimit = session.goal.daysRemaining

  const backstories: Record<string, string> = {
    paper_hands: `You just discovered crypto Twitter at 3 AM while doom-scrolling. Your $${startingAmount.toLocaleString()} life savings (from selling your Nintendo Switch collection) is burning a hole in your wallet. The trenches are calling, anon. Time to find out if you're built different or just different.`,
    diamond_hands: `After months of lurking in Discord alpha groups and pretending to understand what "tokenomics" means, you've finally scraped together $${startingAmount.toLocaleString()}. Your mom thinks you're investing in "the future of money." Your dad still doesn't know what a blockchain is. Time to prove them... something.`,
    gigabrain: `Legend has it you once called the bottom on a token that did 1000x. (It was luck, but we don't talk about that.) With $${startingAmount.toLocaleString()} and an unhealthy caffeine addiction, you're ready to speedrun your way to financial freedom or a therapy session. Probably both.`,
  }

  const funFactsByDifficulty: Record<string, string[]> = {
    paper_hands: [
      "Fun fact: 73% of traders who paper hand end up buying back higher. Don't be a statistic.",
      "Your starting portfolio could buy 2,847 cups of gas station coffee. Choose wisely.",
      "The trenches have no ceiling, but they definitely have a floor. It's called zero.",
    ],
    diamond_hands: [
      `You need to ${multiplier}x your money. That's like turning a Honda Civic into a Lambo.`,
      "Diamond hands are only impressive if you're holding something that goes up.",
      "Remember: Every whale was once a shrimp with conviction (and probably an early airdrop).",
    ],
    gigabrain: [
      "Your brain is so wrinkled it picks up WiFi signals.",
      `${multiplier}x in ${daysLimit} days? Some would call that impossible. You call it Tuesday.`,
      "The real alpha was the sleep deprivation we gained along the way.",
    ],
  }

  return {
    backstory: backstories[difficulty] ?? backstories.diamond_hands ?? '',
    funFacts: funFactsByDifficulty[difficulty] ?? funFactsByDifficulty.diamond_hands ?? [],
    locationName: 'The Trenches',
    quirkyDetail: 'Your desk is covered in empty energy drink cans and sticky notes with "HODL" written on them.',
    motivation: 'You need to prove that your "research" was actually worth something.',
  }
}

const tips = [
  "Choose your own actions and destiny",
  "Your choices dynamically affect progress and outcome",
  "Trade tokens to make it big",
]

export const OnboardingModal: FC<Props> = ({ session, onClose, onDontShowAgain }) => {
  const [step, setStep] = useState(0)
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const [backstory, setBackstory] = useState<GeneratedBackstory | null>(null)
  const [isLoadingBackstory, setIsLoadingBackstory] = useState(true)

  // Fetch AI-generated backstory on mount
  useEffect(() => {
    async function fetchBackstory() {
      try {
        setIsLoadingBackstory(true)
        const generated = await generateBackstoryViaCloud(
          session.difficulty,
          session.startingAmount,
          session.goal.targetAmount,
          session.goal.daysRemaining
        )
        setBackstory(generated)
      } catch (err) {
        console.error('Failed to generate AI backstory, using fallback:', err)
        setBackstory(getFallbackBackstory(session))
      } finally {
        setIsLoadingBackstory(false)
      }
    }

    fetchBackstory()
  }, [session])

  const handleClose = () => {
    if (dontShowAgain) {
      setSkipOnboarding(true)
      onDontShowAgain()
    }
    onClose()
  }

  const handleSkip = () => {
    handleClose()
  }

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1)
    } else {
      handleClose()
    }
  }

  // Use fallback if still loading
  const displayBackstory = backstory || getFallbackBackstory(session)

  const steps = [
    // Step 0: Welcome & Mission
    {
      title: 'WELCOME TO THE TRENCHES',
      content: (
        <div className="space-y-3">
          <div className="p-3 bg-game-accent/10 border border-game-accent/30 text-center">
            <PixelText size="sm" color="accent">YOUR MISSION</PixelText>
            <p className="font-retro text-xl text-game-text mt-2">
              Turn <span className="text-game-gold font-bold">${session.startingAmount.toLocaleString()}</span> into{' '}
              <span className="text-game-profit font-bold">${session.goal.targetAmount.toLocaleString()}</span>
            </p>
            <p className="font-retro text-lg text-game-muted mt-1">
              in <span className="text-game-accent font-bold">{session.goal.daysRemaining} days</span> ({((session.goal.targetAmount / session.startingAmount)).toFixed(0)}x)
            </p>
          </div>
          {isLoadingBackstory ? (
            <div className="flex items-center justify-center py-2">
              <div className="w-5 h-5 border-2 border-game-accent/30 border-t-game-accent rounded-full animate-spin" />
            </div>
          ) : (
            <p className="font-retro text-base text-game-muted text-center italic">
              "{displayBackstory.motivation || 'Time to prove you\'re built different.'}"
            </p>
          )}
        </div>
      ),
    },
    // Step 1: How to Play
    {
      title: 'HOW TO PLAY',
      content: (
        <div className="space-y-3">
          <div className="p-3 bg-purple-500/10 border border-purple-500/30 text-center">
            <p className="font-retro text-lg text-game-text">
              Powered by <span className="text-game-accent font-bold">AI</span> - every game is unique
            </p>
            <p className="font-retro text-sm text-game-muted mt-1">
              No two playthroughs are ever the same
            </p>
          </div>
          <div className="space-y-2">
            {tips.map((tip, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="font-pixel text-game-accent text-sm">{i + 1}.</span>
                <p className="font-retro text-lg text-game-text">{tip}</p>
              </div>
            ))}
          </div>
          <div className="p-3 bg-game-profit/10 border border-game-profit/30 text-center">
            <PixelText size="sm" color="profit">STARTING BALANCE</PixelText>
            <p className="font-mono text-3xl text-game-profit mt-1">
              ${session.startingAmount.toLocaleString()}
            </p>
          </div>
        </div>
      ),
    },
  ]

  const currentStep = steps[step]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-2 sm:p-4">
      <RetroWindow
        title={currentStep?.title || 'WELCOME'}
        className="w-full max-w-md"
      >
        {/* Progress dots */}
        <div className="flex justify-center gap-3 mb-3">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full transition-colors ${
                i === step ? 'bg-game-accent' : 'bg-game-border'
              }`}
            />
          ))}
        </div>

        {/* Content - no scroll, fits on screen */}
        <div className="mb-4">
          {currentStep?.content}
        </div>

        {/* Actions - always visible */}
        <div className="space-y-3 pt-3 border-t border-game-border">
          <div className="flex gap-2">
            <RetroButton
              variant="default"
              size="md"
              className="flex-1 text-lg py-3"
              onClick={handleSkip}
            >
              SKIP
            </RetroButton>
            <RetroButton
              variant="primary"
              size="md"
              className="flex-1 text-lg py-3"
              onClick={handleNext}
              disabled={isLoadingBackstory && step === 0}
            >
              {step < steps.length - 1 ? 'NEXT' : 'LET\'S GO'}
            </RetroButton>
          </div>

          {/* Don't show again checkbox */}
          <label className="flex items-center gap-2 cursor-pointer justify-center">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-5 h-5 accent-game-accent"
            />
            <span className="font-retro text-base text-game-muted">
              Don't show this again
            </span>
          </label>
        </div>
      </RetroWindow>
    </div>
  )
}

export default OnboardingModal
